import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Settings, Thermometer, Droplets, Gauge, Battery, BatteryFull, BatteryCharging, Zap, AlertTriangle, Check, CheckCheck, Clock, Activity, MapPin, Satellite, Lock, Route, Navigation, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { DeviceStatus } from '@/components/devices/DeviceStatus';
import { LocationTrail } from '@/components/maps/LocationTrail';
import { TelemetryChart } from '@/components/charts/TelemetryChart';
import { PowerChart } from '@/components/charts/PowerChart';
import { GaugeCard } from '@/components/charts/GaugeCard';
import { CommandPanel } from '@/components/commands/CommandPanel';
import { ConfigPanel } from '@/components/config/ConfigPanel';
import { JourneyMap, JourneySelector, LocationHistoryTable } from '@/components/journeys';
import { useDevice } from '@/hooks/useDevices';
import { useTelemetry, useLocationHistory, usePowerHistory, useHealthHistory } from '@/hooks/useTelemetry';
import { useJourneys, useJourneyDetail, useLocationHistoryFull, useLatestJourney, useMapMatch, useDeleteJourney } from '@/hooks/useJourneys';
import { useVisitedCities } from '@/hooks/useVisitedCities';
import { VisitedCitiesMap } from '@/components/maps/VisitedCitiesMap';
import { useCommands } from '@/hooks/useCommands';
import { useDeviceAlerts, useAcknowledgeAlert, useBulkAcknowledgeAlerts } from '@/hooks/useAlerts';
import { useIsAdmin, useCanSendCommands } from '@/hooks/useAuth';
import { fetchAuthSession } from 'aws-amplify/auth';
import { usePreferences } from '@/contexts/PreferencesContext';
import {
  formatBattery,
  formatMode,
  formatRelativeTime,
  truncateDeviceUid,
  convertTemperature,
  getTemperatureUnit,
} from '@/utils/formatters';
import { getLocationSourceInfo } from '@/utils/locationSource';
import type { Alert, HealthPoint } from '@/types';

const alertTypeLabels: Record<string, string> = {
  temp_high: 'High Temperature',
  temp_low: 'Low Temperature',
  humidity_high: 'High Humidity',
  humidity_low: 'Low Humidity',
  pressure_change: 'Pressure Change',
  low_battery: 'Low Battery',
  motion: 'Motion Detected',
  gps_power_save: 'GPS Power Save',
  gps_no_sat: 'Unable to obtain GPS location',
};

const healthMethodLabels: Record<string, string> = {
  dfu: 'Firmware Update',
  boot: 'Device Boot',
  reboot: 'Device Reboot',
  reset: 'Device Reset',
  usb: 'USB Connected',
  battery: 'Battery Status',
  sync: 'Sync Event',
  connected: 'Connected',
  disconnected: 'Disconnected',
};


interface DeviceDetailProps {
  mapboxToken: string;
}

export function DeviceDetail({ mapboxToken }: DeviceDetailProps) {
  const { serialNumber } = useParams<{ serialNumber: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { preferences } = usePreferences();
  const tempUnit = preferences.temp_unit === 'fahrenheit' ? 'F' : 'C';

  // Read journey ID from URL query param
  const journeyIdFromUrl = searchParams.get('journey');
  const initialJourneyId = journeyIdFromUrl ? parseInt(journeyIdFromUrl, 10) : null;

  const [showConfig, setShowConfig] = useState(false);
  const [timeRange, setTimeRange] = useState<number | null>(null);
  const [chartTab, setChartTab] = useState('telemetry');
  const [locationTab, setLocationTab] = useState(journeyIdFromUrl ? 'journeys' : 'current');
  const [selectedJourneyId, setSelectedJourneyId] = useState<number | null>(initialJourneyId);

  // Update URL when journey selection changes. Wrapped in useCallback so the
  // stable reference can be safely listed in useEffect dependency arrays.
  // Uses the functional form of setSearchParams to avoid stale closure over
  // the searchParams snapshot captured at render time.
  const handleJourneySelect = useCallback((journeyId: number | null) => {
    setSelectedJourneyId(journeyId);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (journeyId) {
        next.set('journey', journeyId.toString());
      } else {
        next.delete('journey');
      }
      return next;
    });
  }, [setSearchParams]);

  // Set default time range from preferences once loaded
  useEffect(() => {
    if (timeRange === null && preferences.default_time_range) {
      setTimeRange(Number(preferences.default_time_range));
    }
  }, [preferences.default_time_range, timeRange]);

  // Use 24h as fallback until preferences are loaded
  const effectiveTimeRange = timeRange ?? 24;

  // Get journeys data first to calculate time range for journey viewing
  const { data: journeysData, isLoading: journeysLoading } = useJourneys(serialNumber!);
  const journeys = journeysData?.journeys || [];

  // Find selected journey to calculate needed time range
  const selectedJourney = selectedJourneyId
    ? journeys.find(j => j.journey_id === selectedJourneyId)
    : null;

  // Clear invalid journey ID from URL (journey doesn't exist for this device)
  useEffect(() => {
    if (
      !journeysLoading &&
      journeys.length > 0 &&
      selectedJourneyId &&
      !selectedJourney
    ) {
      // Journey ID in URL doesn't exist, clear it
      handleJourneySelect(null);
      setLocationTab('current');
    }
  }, [journeysLoading, journeys, selectedJourneyId, selectedJourney, handleJourneySelect]);

  // Calculate time range needed to cover the journey (if viewing one)
  const journeyTimeRangeHours = selectedJourney
    ? Math.ceil((Date.now() - new Date(selectedJourney.start_time).getTime()) / (1000 * 60 * 60)) + 1
    : null;

  // Use journey time range when viewing a journey, otherwise use user's selected range
  const dataTimeRange = (locationTab === 'journeys' && journeyTimeRangeHours)
    ? Math.max(journeyTimeRangeHours, effectiveTimeRange)
    : effectiveTimeRange;

  // Use higher limit when viewing journeys or longer time ranges to ensure we get all data
  // Default 1000 limit can miss older data when there are many recent records or a wide time window
  const telemetryLimit = (locationTab === 'journeys' && selectedJourneyId)
    ? 10000
    : dataTimeRange >= 168 ? 10000 : dataTimeRange >= 24 ? 2000 : 1000;

  const { data: device, isLoading: deviceLoading } = useDevice(serialNumber!);
  const { data: telemetryData, isLoading: telemetryLoading } = useTelemetry(
    serialNumber!,
    dataTimeRange,
    telemetryLimit
  );
  const { data: locationData } = useLocationHistory(serialNumber!, dataTimeRange);
  // Use higher limit when viewing journeys or longer time ranges to ensure we get all data
  const powerLimit = (locationTab === 'journeys' && selectedJourneyId)
    ? 10000
    : dataTimeRange >= 168 ? 10000 : dataTimeRange >= 24 ? 2000 : 1000;
  const { data: powerData, isLoading: powerLoading } = usePowerHistory(serialNumber!, dataTimeRange, powerLimit);
  const { data: healthData, isLoading: healthLoading } = useHealthHistory(serialNumber!, Math.max(dataTimeRange, 168)); // At least 7 days for health
  const { data: commandsData } = useCommands(serialNumber!);
  const { data: alertsData } = useDeviceAlerts(serialNumber!);
  const acknowledgeMutation = useAcknowledgeAlert();
  const bulkAcknowledgeMutation = useBulkAcknowledgeAlerts();

  // Journey and location history hooks
  const { data: journeyDetailData, isLoading: journeyDetailLoading } = useJourneyDetail(serialNumber!, selectedJourneyId);
  const { data: locationHistoryData, isLoading: locationHistoryLoading } = useLocationHistoryFull(serialNumber!, dataTimeRange);
  const { data: latestJourney } = useLatestJourney(serialNumber!);
  const mapMatchMutation = useMapMatch(serialNumber!, selectedJourneyId);
  const deleteJourneyMutation = useDeleteJourney();
  const { data: visitedCitiesData, isLoading: visitedCitiesLoading } = useVisitedCities(serialNumber!);
  const visitedCities = visitedCitiesData?.cities || [];

  // Check if user can delete journeys (admin or device owner)
  const { isAdmin } = useIsAdmin();
  const { canSend: canSendCommands } = useCanSendCommands();
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);

  useEffect(() => {
    fetchAuthSession().then((session) => {
      const email = session.tokens?.idToken?.payload?.email as string | undefined;
      setCurrentUserEmail(email || null);
    }).catch(() => setCurrentUserEmail(null));
  }, []);

  const canDeleteJourneys = isAdmin || (currentUserEmail && device?.assigned_to === currentUserEmail);

  const telemetry = telemetryData?.telemetry || [];
  const alerts = alertsData?.alerts || [];
  const activeAlerts = alerts.filter((a: Alert) => a.acknowledged === 'false' || a.acknowledged === false);
  const locations = locationData?.locations || [];
  const power = powerData?.power || [];
  const health = healthData?.health || [];
  const lastCommand = commandsData?.commands?.[0];

  // Journey data
  const journeyPoints = journeyDetailData?.points || [];
  const locationHistory = locationHistoryData?.locations || [];

  // Determine if we're viewing a journey (for filtering and UI)
  const isViewingJourney = locationTab === 'journeys' && selectedJourney;

  // Filter data to journey timeframe when a journey is selected
  const filteredTelemetry = isViewingJourney && selectedJourney
    ? telemetry.filter(t => {
        const time = new Date(t.time).getTime();
        const startTime = new Date(selectedJourney.start_time).getTime();
        const endTime = selectedJourney.end_time
          ? new Date(selectedJourney.end_time).getTime()
          : Date.now();
        return time >= startTime && time <= endTime;
      })
    : telemetry;

  const filteredPower = isViewingJourney && selectedJourney
    ? power.filter(p => {
        const time = new Date(p.time).getTime();
        const startTime = new Date(selectedJourney.start_time).getTime();
        const endTime = selectedJourney.end_time
          ? new Date(selectedJourney.end_time).getTime()
          : Date.now();
        return time >= startTime && time <= endTime;
      })
    : power;

  const filteredHealth = isViewingJourney && selectedJourney
    ? health.filter(h => {
        const time = new Date(h.time).getTime();
        const startTime = new Date(selectedJourney.start_time).getTime();
        const endTime = selectedJourney.end_time
          ? new Date(selectedJourney.end_time).getTime()
          : Date.now();
        return time >= startTime && time <= endTime;
      })
    : health;

  // Get latest values and sparkline data
  // Find first telemetry record with actual sensor data (not just location-only events)
  const telemetryWithSensorData = telemetry.find(t =>
    t.temperature !== undefined || t.humidity !== undefined || t.pressure !== undefined
  );
  // Use telemetry API data if available, otherwise fall back to device.last_telemetry
  const latestTelemetry = telemetryWithSensorData || (device ? {
    temperature: device.temperature,
    humidity: device.humidity,
    pressure: device.pressure,
    voltage: device.voltage,
    time: device.last_seen,
  } : undefined);
  const sparklineTemp = telemetry.slice(0, 20).map((t) => convertTemperature(t.temperature, tempUnit) || 0);
  const sparklineHumidity = telemetry.slice(0, 20).map((t) => t.humidity || 0);

  const battery = formatBattery(device?.voltage);

  if (deviceLoading || !device) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Loading device...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1 min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/">
              <Button variant="ghost" size="icon" className="flex-shrink-0">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2 min-w-0">
              {device.usb_powered ? (
                <span title="USB Powered" className="flex-shrink-0"><BatteryCharging className="h-5 w-5 text-blue-500" /></span>
              ) : (
                <span title="Battery Powered" className="flex-shrink-0"><BatteryFull className="h-5 w-5 text-green-500" /></span>
              )}
              <span className="truncate">{device.name || device.serial_number || truncateDeviceUid(device.device_uid)}</span>
            </h1>
            <DeviceStatus status={device.status} className="flex-shrink-0" />
            {activeAlerts.length > 0 && (
              <Badge variant="destructive" className="gap-1 flex-shrink-0">
                <AlertTriangle className="h-3 w-3" />
                {activeAlerts.length} Alert{activeAlerts.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-2">
            {device.fleet_name && <span>Fleet: {device.fleet_name}</span>}
            {(device.assigned_to_name || device.assigned_to) && (
              <>
                {device.fleet_name && <span>•</span>}
                <span>Assigned: {device.assigned_to_name || device.assigned_to}</span>
              </>
            )}
            <Badge
              variant={device.transit_locked || device.demo_locked ? "default" : "secondary"}
              className={
                device.transit_locked
                  ? "gap-1 bg-amber-500 hover:bg-amber-600"
                  : device.demo_locked
                  ? "gap-1 bg-green-500 hover:bg-green-600"
                  : ""
              }
            >
              {(device.transit_locked || device.demo_locked) && <Lock className="h-3 w-3" />}
              {formatMode(device.mode)}
            </Badge>
            {device.pending_mode && device.pending_mode !== device.mode && (
              <Badge variant="outline" className="gap-1 border-blue-300 bg-blue-50 text-blue-700 animate-pulse">
                <ArrowRight className="h-3 w-3" />
                {formatMode(device.pending_mode)}
              </Badge>
            )}
            {device.gps_power_saving && device.mode === 'transit' && (
              <Badge variant="outline" className="gap-1 bg-yellow-50 border-yellow-200 text-yellow-700">
                <Satellite className="h-3 w-3" />
                GPS Off
              </Badge>
            )}
            {device.gps_no_sat && device.mode === 'transit' && (
              <Badge variant="outline" className="gap-1 bg-orange-50 border-orange-200 text-orange-700">
                <Satellite className="h-3 w-3" />
                No GPS
              </Badge>
            )}
            {device.last_seen && (
              <>
                <span>•</span>
                <span>Last seen: {formatRelativeTime(device.last_seen)}</span>
              </>
            )}
          </div>
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => setShowConfig(!showConfig)}
            className="flex-shrink-0 flex-1 sm:flex-none"
          >
            <Settings className="h-4 w-4 mr-2" />
            Config
          </Button>
          <Button
            variant="outline"
            asChild
            className="flex-shrink-0 flex-1 sm:flex-none"
          >
            <a
              href={`https://notehub.io/project/app:b5b8fc4a-d8ca-4bd8-84ad-39563006635d/devices/${device.device_uid}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View in Notehub
            </a>
          </Button>
        </div>
      </div>

      {/* Config Panel - Mobile (shown at top) */}
      {showConfig && (
        <div className="lg:hidden mb-6">
          <ConfigPanel
            serialNumber={device.serial_number}
            assignedTo={device.assigned_to}
            pendingMode={device.pending_mode}
            currentMode={device.mode}
            onClose={() => setShowConfig(false)}
          />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3 w-full">
        {/* Main Content */}
        <div className={`${showConfig ? 'lg:col-span-2' : 'lg:col-span-3'} min-w-0`}>
          {/* Current Readings */}
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-6 w-full">
            <GaugeCard
              title="Temperature"
              value={convertTemperature(latestTelemetry?.temperature, tempUnit)?.toFixed(1) || '--'}
              unit={getTemperatureUnit(tempUnit)}
              icon={<Thermometer className="h-4 w-4 text-orange-500" />}
              sparklineData={sparklineTemp}
              status={
                latestTelemetry?.temperature && latestTelemetry.temperature > 35
                  ? 'warning'
                  : 'normal'
              }
            />
            <GaugeCard
              title="Humidity"
              value={latestTelemetry?.humidity?.toFixed(1) || '--'}
              unit="%"
              icon={<Droplets className="h-4 w-4 text-blue-500" />}
              sparklineData={sparklineHumidity}
            />
            <GaugeCard
              title="Pressure"
              value={latestTelemetry?.pressure?.toFixed(0) || '--'}
              unit="hPa"
              icon={<Gauge className="h-4 w-4 text-purple-500" />}
            />
            <GaugeCard
              title="Battery"
              value={battery.percentage}
              unit="%"
              icon={
                <Battery
                  className={`h-4 w-4 ${
                    battery.level === 'critical'
                      ? 'text-red-500'
                      : battery.level === 'low'
                      ? 'text-yellow-500'
                      : 'text-green-500'
                  }`}
                />
              }
              status={
                battery.level === 'critical'
                  ? 'critical'
                  : battery.level === 'low'
                  ? 'warning'
                  : 'normal'
              }
            />
          </div>

          {/* Location / Journeys Section */}
          <Card className="mb-6">
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Location
              </CardTitle>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
                <Tabs value={locationTab} onValueChange={setLocationTab} className="w-full sm:w-auto">
                  <TabsList className="grid grid-cols-4 w-full sm:w-auto sm:inline-flex">
                    <TabsTrigger value="current" className="text-xs sm:text-sm">
                      <MapPin className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline">Current</span>
                    </TabsTrigger>
                    <TabsTrigger value="journeys" className="text-xs sm:text-sm">
                      <Route className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline">Journeys</span>
                    </TabsTrigger>
                    <TabsTrigger value="cities" className="text-xs sm:text-sm">
                      <Navigation className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline">Cities</span>
                    </TabsTrigger>
                    <TabsTrigger value="history" className="text-xs sm:text-sm">
                      <Clock className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline">History</span>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                {locationTab === 'current' && device.location_source && (
                  (() => {
                    const sourceInfo = getLocationSourceInfo(device.location_source);
                    const SourceIcon = sourceInfo.icon;
                    return (
                      <Badge variant="outline" className={`gap-1 ${sourceInfo.bgColor} border-0 self-start sm:self-auto`}>
                        <SourceIcon className={`h-3 w-3 ${sourceInfo.color}`} />
                        <span className={sourceInfo.color}>{sourceInfo.label}</span>
                      </Badge>
                    );
                  })()
                )}
              </div>
            </CardHeader>
            <CardContent className={locationTab === 'current' ? 'p-0' : ''}>
              {locationTab === 'current' && (
                <>
                  {latestJourney ? (
                    <LocationTrail
                      locations={locations.filter(l => (l as any).journey_id === latestJourney.journey_id).map(l => ({
                        time: l.time,
                        lat: l.lat,
                        lon: l.lon,
                        source: l.source,
                      }))}
                      currentLocation={device.latitude && device.longitude ? { lat: device.latitude, lon: device.longitude } : undefined}
                      mapboxToken={mapboxToken}
                      className="h-[300px] rounded-b-lg overflow-hidden"
                    />
                  ) : (
                    <LocationTrail
                      locations={[]}
                      currentLocation={device.latitude && device.longitude ? { lat: device.latitude, lon: device.longitude } : undefined}
                      mapboxToken={mapboxToken}
                      className="h-[300px] rounded-b-lg overflow-hidden"
                    />
                  )}
                </>
              )}
              {locationTab === 'history' && (
                <LocationHistoryTable
                  locations={locationHistory}
                  isLoading={locationHistoryLoading}
                />
              )}
              {locationTab === 'cities' && (
                <>
                  {visitedCitiesLoading ? (
                    <div className="h-[400px] flex items-center justify-center">
                      <span className="text-muted-foreground">Loading visited cities...</span>
                    </div>
                  ) : visitedCities.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>{visitedCities.length} unique {visitedCities.length === 1 ? 'city' : 'cities'} visited</span>
                        {visitedCitiesData?.totalLocations && (
                          <span>{visitedCitiesData.totalLocations.toLocaleString()} total location records</span>
                        )}
                      </div>
                      <VisitedCitiesMap
                        cities={visitedCities}
                        mapboxToken={mapboxToken}
                        className="h-[400px] rounded-lg overflow-hidden border"
                      />
                    </div>
                  ) : (
                    <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                      <div className="text-center">
                        <Navigation className="h-8 w-8 mx-auto mb-2" />
                        <p>No visited cities recorded yet</p>
                      </div>
                    </div>
                  )}
                </>
              )}
              {locationTab === 'journeys' && (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                  <div className="lg:col-span-1">
                    <h4 className="text-sm font-medium mb-3">Select a Journey</h4>
                    <JourneySelector
                      journeys={journeys}
                      selectedJourneyId={selectedJourneyId}
                      onSelect={handleJourneySelect}
                      isLoading={journeysLoading}
                      canDelete={!!canDeleteJourneys}
                      onDelete={(journeyId) => {
                        deleteJourneyMutation.mutate({ serialNumber: serialNumber!, journeyId });
                        // Clear selection if we deleted the selected journey
                        if (selectedJourneyId === journeyId) {
                          handleJourneySelect(null);
                        }
                      }}
                      isDeleting={deleteJourneyMutation.isPending}
                    />
                  </div>
                  <div className="lg:col-span-3">
                    <h4 className="text-sm font-medium mb-3">Journey Playback</h4>
                    {selectedJourneyId ? (
                      journeyDetailLoading ? (
                        <div className="h-[400px] flex items-center justify-center border rounded-lg">
                          <span className="text-muted-foreground">Loading journey...</span>
                        </div>
                      ) : (
                        <JourneyMap
                          points={journeyPoints}
                          mapboxToken={mapboxToken}
                          className="h-[500px] border rounded-lg overflow-hidden"
                          matchedRoute={journeyDetailData?.journey?.matched_route}
                          matchedPointsCount={journeyDetailData?.journey?.matched_points_count}
                          onMatchRoute={() => mapMatchMutation.mutate()}
                          isMatching={mapMatchMutation.isPending}
                          power={journeyDetailData?.power}
                        />
                      )
                    ) : (
                      <div className="h-[400px] flex items-center justify-center border rounded-lg text-muted-foreground">
                        <div className="text-center">
                          <Navigation className="h-8 w-8 mx-auto mb-2" />
                          <p>Select a journey to view playback</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Historical Charts */}
          <Card className="mb-6 overflow-hidden">
            <CardHeader className="flex flex-col gap-3">
              <CardTitle className="whitespace-nowrap flex items-center gap-2">
                {isViewingJourney ? (
                  <>
                    <Route className="h-5 w-5" />
                    Journey Data
                  </>
                ) : (
                  'Historical Data'
                )}
              </CardTitle>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Tabs value={chartTab} onValueChange={setChartTab} className="w-full sm:w-auto">
                  <TabsList className="grid grid-cols-3 w-full sm:w-auto sm:inline-flex">
                    <TabsTrigger value="telemetry" className="text-xs sm:text-sm">
                      <Thermometer className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline">Telemetry</span>
                    </TabsTrigger>
                    <TabsTrigger value="power" className="text-xs sm:text-sm">
                      <Zap className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline">Power</span>
                    </TabsTrigger>
                    <TabsTrigger value="health" className="text-xs sm:text-sm">
                      <Activity className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline">Health</span>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                {!isViewingJourney && (
                  <Tabs value={String(effectiveTimeRange)} onValueChange={(v) => setTimeRange(Number(v))} className="w-full sm:w-auto">
                    <TabsList className="grid grid-cols-4 w-full sm:w-auto sm:inline-flex">
                      <TabsTrigger value="1" className="text-xs sm:text-sm px-1.5 sm:px-3">1h</TabsTrigger>
                      <TabsTrigger value="4" className="text-xs sm:text-sm px-1.5 sm:px-3">4h</TabsTrigger>
                      <TabsTrigger value="24" className="text-xs sm:text-sm px-1.5 sm:px-3">24h</TabsTrigger>
                      <TabsTrigger value="168" className="text-xs sm:text-sm px-1.5 sm:px-3">7d</TabsTrigger>
                    </TabsList>
                  </Tabs>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {chartTab === 'telemetry' && (
                <>
                  {telemetryLoading ? (
                    <div className="h-[300px] flex items-center justify-center">
                      <span className="text-muted-foreground">Loading chart...</span>
                    </div>
                  ) : filteredTelemetry.length > 0 ? (
                    <TelemetryChart
                      data={filteredTelemetry}
                      showTemperature
                      showHumidity
                      showPressure
                      height={300}
                      tempUnit={tempUnit}
                      hours={dataTimeRange}
                    />
                  ) : (
                    <div className="h-[300px] flex items-center justify-center">
                      <span className="text-muted-foreground">
                        {isViewingJourney ? 'No telemetry data during this journey' : 'No telemetry data available'}
                      </span>
                    </div>
                  )}
                </>
              )}
              {chartTab === 'power' && (
                <>
                  {powerLoading ? (
                    <div className="h-[300px] flex items-center justify-center">
                      <span className="text-muted-foreground">Loading chart...</span>
                    </div>
                  ) : filteredPower.length > 0 ? (
                    <PowerChart data={filteredPower} hours={dataTimeRange} />
                  ) : (
                    <div className="h-[300px] flex items-center justify-center">
                      <span className="text-muted-foreground">
                        {isViewingJourney ? 'No power data during this journey' : 'No power data available (Mojo required)'}
                      </span>
                    </div>
                  )}
                </>
              )}
              {chartTab === 'health' && (
                <>
                  {healthLoading ? (
                    <div className="h-[300px] flex items-center justify-center">
                      <span className="text-muted-foreground">Loading health events...</span>
                    </div>
                  ) : filteredHealth.length > 0 ? (
                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                      {filteredHealth.map((event: HealthPoint, index: number) => (
                        <div
                          key={`${event.time}-${index}`}
                          className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30"
                        >
                          <Activity className="h-4 w-4 mt-0.5 text-blue-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="text-xs">
                                {healthMethodLabels[event.method || ''] || event.method || 'Unknown'}
                              </Badge>
                              {event.voltage_mode && (
                                <Badge variant="outline" className="text-xs">
                                  {event.voltage_mode}
                                </Badge>
                              )}
                            </div>
                            {event.text && (
                              <p className="text-sm mt-1 break-words text-muted-foreground">
                                {event.text}
                              </p>
                            )}
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatRelativeTime(new Date(event.time))}
                              </span>
                              {event.voltage !== undefined && (
                                <span>{event.voltage.toFixed(2)}V</span>
                              )}
                              {event.milliamp_hours !== undefined && (
                                <span>{event.milliamp_hours.toFixed(3)} mAh</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center">
                      <span className="text-muted-foreground">
                        {isViewingJourney ? 'No health events during this journey' : 'No health events recorded'}
                      </span>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Command Panel - Only visible to non-Viewer roles */}
          {canSendCommands && (
            <CommandPanel
              serialNumber={device.serial_number}
              audioEnabled={device.audio_enabled !== false}
              lastCommand={lastCommand}
              transitLocked={device.transit_locked}
              demoLocked={device.demo_locked}
              assignedTo={device.assigned_to}
            />
          )}

          {/* Alerts Section */}
          <Card className="mt-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Alerts
                {activeAlerts.length > 0 && (
                  <Badge variant="destructive">{activeAlerts.length} Active</Badge>
                )}
              </CardTitle>
              {canSendCommands && activeAlerts.length > 1 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={bulkAcknowledgeMutation.isPending}
                    >
                      <CheckCheck className="h-4 w-4 mr-1" />
                      Ack All
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Acknowledge All Alerts</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will acknowledge {activeAlerts.length} active alert{activeAlerts.length !== 1 ? 's' : ''} for this device.
                        This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => {
                          const alertIds = activeAlerts.map((a: Alert) => a.alert_id);
                          bulkAcknowledgeMutation.mutate({ alertIds });
                        }}
                      >
                        Acknowledge All
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Check className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <p>No alerts for this device</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.slice(0, 10).map((alert: Alert) => {
                    const isAcknowledged = alert.acknowledged === 'true' || alert.acknowledged === true;
                    return (
                      <div
                        key={alert.alert_id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          isAcknowledged ? 'bg-muted/50 opacity-60' : 'bg-destructive/5 border-destructive/20'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <AlertTriangle
                            className={`h-4 w-4 ${isAcknowledged ? 'text-muted-foreground' : 'text-destructive'}`}
                          />
                          <div>
                            <p className="font-medium text-sm">
                              {alertTypeLabels[alert.type] || alert.type}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {formatRelativeTime(new Date(alert.created_at))}
                              {alert.value !== undefined && (
                                <span>• Value: {alert.value.toFixed(1)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        {!isAcknowledged && canSendCommands && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => acknowledgeMutation.mutate({ alertId: alert.alert_id })}
                            disabled={acknowledgeMutation.isPending}
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Ack
                          </Button>
                        )}
                        {isAcknowledged && (
                          <Badge variant="secondary" className="text-xs">
                            <Check className="h-3 w-3 mr-1" />
                            Acknowledged
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                  {alerts.length > 10 && (
                    <p className="text-center text-sm text-muted-foreground">
                      Showing 10 of {alerts.length} alerts
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Device Info */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Device Information</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-muted-foreground">Device UID</dt>
                  <dd className="font-mono">{device.device_uid}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Serial Number</dt>
                  <dd>
                    {device.serial_number || '--'}
                    {device.assigned_to_name && (
                      <span className="text-muted-foreground ml-1">({device.assigned_to_name})</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Firmware</dt>
                  <dd>{device.firmware_version || '--'}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Notecard</dt>
                  <dd>
                    {device.notecard_version || '--'}
                    {device.notecard_sku && <span className="text-muted-foreground ml-1">({device.notecard_sku})</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Battery</dt>
                  <dd>{battery.voltage} ({battery.percentage}%)</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Audio</dt>
                  <dd>
                    {device.audio_enabled !== false ? 'Enabled' : 'Disabled'}
                    {device.audio_enabled && device.audio_volume && ` (${device.audio_volume}%)`}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </div>

        {/* Config Panel - Desktop (shown in sidebar) */}
        {showConfig && (
          <div className="hidden lg:block lg:col-span-1">
            <ConfigPanel
              serialNumber={device.serial_number}
              assignedTo={device.assigned_to}
              onClose={() => setShowConfig(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
